import {
  PdfGen,
  PdfSize,
  PdfRect,
  PdfPoint,
  PdfBrush,
  PdfFont,
  ETextAlign,
  PdfUnit,
} from '../../pdf/pdf';
import { ILayoutListGen } from '../layoutList';
import { IItemCount } from '@educraft/base';
import { OptsList, getListItems } from '../optsList';
import { GUtil } from '../../pdf/gutil';
import { calcOrigin } from '../../core/genHelper';
import '../../pdf/draw/primitives';
import '../../pdf/draw/string';

/**
 * Sekvencni_skladacka (Sequential Puzzle)
 *
 * Layout: Vertical strips with puzzle-like teeth between items.
 * Students cut out the strips and assemble them in the correct sequence.
 * The teeth are generated as random boolean combinations to ensure only
 * the correct adjacent items fit perfectly.
 *
 * Migrated from C# EduFrame.Order.Generators.Sekvencni_skladacka
 */
export class SekvencniSkladacka implements ILayoutListGen, IItemCount {
  countDefault(): number {
    return 10;
  }
  countMin(): number {
    return 2;
  }
  countMax(): number {
    return 20;
  } // 20 items per page max for legibility

  public async generate(pdf: PdfGen, opts: OptsList): Promise<void> {
    const items = getListItems(opts);
    if (items.length === 0) return;

    const margin = PdfUnit.FromMillimeters(5);
    const tooth = PdfUnit.FromMillimeters(5);
    const toothCount = 5;
    const rnd = opts.data.random;

    // We can fit all items on one page if they are few, but let's support multiple pages
    // if requested count is large. However, Sekvencni_skladacka is usually one sequence.
    // If items.length > 20, we'll just use one page for the first 20 for now,
    // or we could split into multiple sequences.
    const itemsPerPage = Math.min(items.length, 20);

    for (let startIdx = 0; startIdx < items.length; startIdx += itemsPerPage) {
      const page = pdf.addPage();
      const currentItems = items.slice(startIdx, Math.min(startIdx + itemsPerPage, items.length));
      const count = currentItems.length;

      const combinations: boolean[][] = [];
      const totalCombinations = 1 << toothCount;
      const indices = Array.from({ length: totalCombinations }, (_, i) => i);
      // Shuffle indices to pick random combinations
      for (let i = indices.length - 1; i > 0; i--) {
        const j = rnd.next(0, i + 1);
        [indices[i], indices[j]] = [indices[j], indices[i]];
      }

      for (let i = 0; i < count; i++) {
        const comboIdx = indices[i % totalCombinations];
        const combo: boolean[] = [];
        for (let bit = 0; bit < toothCount; bit++) {
          combo.push((comboIdx & (1 << bit)) !== 0);
        }
        combinations.push(combo);
      }

      const { origin, size } = calcOrigin(1, count, margin, PdfSize.A4);
      const font = page.adjustFont(
        size.width,
        currentItems.map((it) => it.value),
        GUtil.baseFont
      );

      for (let i = 0; i < count; i++) {
        const rect = new PdfRect(origin.x, origin.y + i * size.height, size.width, size.height);

        page.drawString(currentItems[i].value, font, PdfBrush.Black, null, rect, ETextAlign.Center);

        // Draw top teeth (connecting to previous item)
        if (i > 0) {
          const points: PdfPoint[] = [];
          const combo = combinations[i];
          const segmentW = size.width / toothCount;

          for (let x = 0; x < toothCount; x++) {
            const b = combo[x];
            const direction = b ? 1 : -1;

            const p1 = new PdfPoint(rect.x + segmentW * x, rect.top);
            const p2 = new PdfPoint(rect.x + segmentW * (x + 0.3), rect.top + direction * tooth);
            const p3 = new PdfPoint(rect.x + segmentW * (x + 0.7), rect.top + direction * tooth);
            const p4 = new PdfPoint(rect.x + segmentW * (x + 1.0), rect.top);

            page.drawLines(GUtil.basePen, [p1, p2, p3, p4]);
          }
        }

        // Draw bottom teeth (connecting to next item)
        if (i < count - 1) {
          // Bottom teeth of current item must match top teeth of NEXT item
          const points: PdfPoint[] = [];
          const nextCombo = combinations[i + 1];
          const segmentW = size.width / toothCount;

          for (let x = 0; x < toothCount; x++) {
            const b = nextCombo[x];
            const direction = b ? 1 : -1;

            const p1 = new PdfPoint(rect.x + segmentW * x, rect.bottom);
            const p2 = new PdfPoint(rect.x + segmentW * (x + 0.3), rect.bottom + direction * tooth);
            const p3 = new PdfPoint(rect.x + segmentW * (x + 0.7), rect.bottom + direction * tooth);
            const p4 = new PdfPoint(rect.x + segmentW * (x + 1.0), rect.bottom);

            page.drawLines(GUtil.basePen, [p1, p2, p3, p4]);
          }
        }

        // Draw side borders
        page.drawLine(
          GUtil.basePen,
          new PdfPoint(rect.left, rect.top),
          new PdfPoint(rect.left, rect.bottom)
        );
        page.drawLine(
          GUtil.basePen,
          new PdfPoint(rect.right, rect.top),
          new PdfPoint(rect.right, rect.bottom)
        );

        // Draw top/bottom borders for the very first/last items
        if (i === 0) {
          page.drawLine(
            GUtil.basePen,
            new PdfPoint(rect.left, rect.top),
            new PdfPoint(rect.right, rect.top)
          );
        }
        if (i === count - 1) {
          page.drawLine(
            GUtil.basePen,
            new PdfPoint(rect.left, rect.bottom),
            new PdfPoint(rect.right, rect.bottom)
          );
        }
      }
    }
  }
}
